import React, { useState, useEffect } from "react";
import GoogleIcon from "../sharedComponents/GoogleIcon";
import InputField from "../sharedComponents/InputField";
import { EnvelopeIcon, LockClosedIcon, ArrowLeftEndOnRectangleIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { loginUser } from "../../slices/1-auth/1-login/thunk";
import logo from "../../assets/images/Logo.webp";
import { useTranslation } from "react-i18next";
import MainButton from "../sharedComponents/MainButton";
import { validateField } from "../../utils/validateForm";
import { validationRegex } from "../../utils/validationRegex";

const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  /* ================= STATIC CONTENT ================= */
  const formContent = {
    title: t("login.title"),
    subtitle: t("login.subtitle"),
    emailLabel: t("login.emailLabel"),
    emailPlaceholder: t("login.emailPlaceholder"),
    passwordLabel: t("login.passwordLabel"),
    passwordPlaceholder: t("login.passwordPlaceholder"),
    rememberMeText: t("login.rememberMeText"),
    forgotPasswordText: t("login.forgotPasswordText"),
    signInText: t("login.signInText"),
    signingInText: t("login.signingInText"),
    googleSignInText: t("login.googleSignInText"),
    additionalText: t("login.additionalText"),
    additionalLinkText: t("login.additionalLinkText"),
    additionalLinkHref: "/sign-up",
    orText: t("login.orText"),
    enterEmailPasswordText: t("login.enterEmailPasswordText"),
    loginFailedText: t("login.loginFailedText"),
  };

  const {
    title,
    subtitle,
    emailLabel,
    emailPlaceholder,
    passwordLabel,
    passwordPlaceholder,
    rememberMeText,
    forgotPasswordText,
    signInText,
    signingInText,
    googleSignInText,
    additionalText,
    additionalLinkText,
    additionalLinkHref,
    orText,
    enterEmailPasswordText,
    loginFailedText,
  } = formContent;

  /* ================= REDUX ================= */
  const { loading, error, token } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!token) return;

    const savedData = localStorage.getItem("bookingFormData");
    if (savedData) {
      const { redirectUrl } = JSON.parse(savedData);
      if (redirectUrl) {
        navigate(redirectUrl, { replace: true });
        return;
      }
    }

    navigate("/", { replace: true });
  }, [token, navigate]);

  /* ================= HANDLERS ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      email: validateField("email", email, validationRegex.signin.email, t),
      password: validateField("password", password, validationRegex.signin.password, t),
    };

    setErrors(newErrors);

    if (newErrors.email || newErrors.password) return;

    try {
      await dispatch(loginUser({ email, password })).unwrap();
    } catch (err) { }
  };

  /* ================= UI ================= */
  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2 ">
      {/* Left Side */}
      <div className="flex-col items-center justify-center hidden min-h-screen text-center md:flex dark:bg-bg-dark-secondary bg-bg-light-secondary">
        <div>
          <img
            src={logo}
            alt={t("login.logoAlt")}
            className="flex items-center justify-center mb-6 rounded-full w-28 h-28 shadow-glow"
          />
        </div>
        <h1 className="mb-4 text-3xl font-bold text-black dark:text-white">{t("login.brandName")}</h1>
        <p className="px-4 text-center text-text-muted-light dark:text-text-muted-dark">
          {t("login.brandSubtitle")}
        </p>
      </div>

      {/* Right Form */}
      <div className="flex items-center justify-center px-6 text-center">
        <div className="w-full max-w-md">
          <h2 className="mb-2 text-3xl font-bold ">{title}</h2>
          <p className="my-4 text-center md:text-md text-text-muted-light dark:text-text-muted-dark">{subtitle}</p>

          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-2 text-start">
              <label>{emailLabel}</label>
              <InputField
                type="email"
                placeholder={emailPlaceholder}
                icon={EnvelopeIcon}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {errors.email && (
                <p className="text-xs text-red-500">{errors.email}</p>
              )}
            </div>
            <div className="flex flex-col gap-2 text-start">
              <label>{passwordLabel}</label>
              <InputField
                type="password"
                placeholder={passwordPlaceholder}
                icon={LockClosedIcon}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {errors.password && (
                <p className="text-xs text-red-500">{errors.password}</p>
              )}
            </div>

            <div className="flex items-center justify-between text-sm text-black dark:text-white">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="appearance-none w-4 h-4 border border-gray-300 rounded-sm dark:checked:bg-primary checked:bg-primary-light dark:checked:border-primary checked:border-primary-light relative
                  before:content-['✓'] before:absolute before:text-dark before:top-[-3px] before:left-[2px] before:opacity-0 checked:before:opacity-100"
                />
                {rememberMeText}
              </label>

              <a href="/forget-password" className="dark:text-primary text-primary-light hover:underline">{forgotPasswordText}</a>
            </div>

            {error && <p className="text-sm text-center text-red-500">{error?.message}</p>}

            <MainButton
              type="submit"
              disabled={loading}
              className="flex items-center justify-center py-3 text-lg font-semibold transition duration-300 dark:bg-primary bg-primary-light dark:text-bg-dark-primary text-bg-light-primary rounded-xl disabled:opacity-70 shadow-glow hover:shadow-glow-hover "
            >
              <ArrowLeftEndOnRectangleIcon className="w-6 h-6 rotate-180 me-2" />
              {loading ? signingInText : signInText}
            </MainButton>

            <div className="flex items-center my-4">
              <hr className="flex-grow border-gray-300" />
              <span className="mx-2 text-gray-400">{orText}</span>
              <hr className="flex-grow border-gray-300" />
            </div>

            <button
              type="button"
              onClick={() =>
                window.location.replace(`${process.env.REACT_APP_API_URL}/google`)
              }
              className="flex items-center justify-center w-full gap-2 px-4 py-3 duration-300 border border-primary dark:hover:text-bg-dark-primary hover:text-bg-light-primary rounded-xl dark:hover:bg-primary hover:bg-primary-light hover:text-dark"
            >
              <GoogleIcon />
              <span className="flex-grow text-center">{googleSignInText}</span>
              <span className="w-5" />
            </button>

            {additionalText && (
              <p className="mt-4 text-sm text-center text-text-muted-light dark:text-text-muted-dark">
                {additionalText}{" "}
                <a href={additionalLinkHref} className="dark:text-primary text-primary-light hover:underline">
                  {additionalLinkText}
                </a>
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
